import React from 'react';
import { fname } from '../utils.js';

// ══════════════════════════════════════════════════════════════════
// Maintenance.jsx — La Conciergerie
// Mode maintenance ciblé : bloque l'accès à l'interface pour les profils
// choisis par l'admin, avec un écran d'attente à l'humour variable.
// L'admin n'est JAMAIS bloqué par ce mécanisme (garde-fou pour toujours
// pouvoir désactiver soi-même, cf. décision Claire).
// Ciblage à deux niveaux :
//   - par rôle (targets: staff/gerant/provider/client)
//   - par compte individuel, en exception d'un rôle bloqué (exceptions:
//     {staff:[ids], gerant:[ids], provider:[ids], client:[ids]} — ces
//     comptes gardent l'accès même si leur rôle est ciblé).
// État synchronisé via Supabase (clé 'maintenance7', voir useSt dans App.jsx) :
//   { on, targets, exceptions, messageId, customE, customT, customS, by, ts }
// ══════════════════════════════════════════════════════════════════

const ROLE_LABELS = { staff: 'Salariés', gerant: 'Direction', provider: 'Prestataires', client: 'Propriétaires' };

const MAINT_MESSAGES = [
  { id: 'menage',  e: '🧹', t: 'Chut, on fait le grand ménage',      s: "Nos elfes numériques sont passés en mode check-out express : literie changée, poussière traquée, application en cours de récupération. On repasse un coup de serpillière et on revient très vite." },
  { id: 'cles',    e: '🔑', t: 'Les clés sont encore au tableau',     s: "L'équipe technique est en pleine remise des clés côté coulisses. L'appli fait sa valise, mais promis, elle n'oublie pas le doudou. Retour dans quelques minutes." },
  { id: 'bretzel', e: '🥨', t: 'Petite pause bretzel',                s: "Le temps d'un café et d'un bretzel encore chaud, on retape l'appli en coulisses. La choucroute n'attend pas, mais vous si, un tout petit peu." },
  { id: 'valise',  e: '🧳', t: "L'appli a fait sa valise",            s: "Direction les coulisses pour quelques ajustements. Elle revient avec le sourire (et sans oublier les clés) d'ici quelques minutes." },
  { id: 'plumeau', e: '🪶', t: 'On dépoussière le moteur',            s: "Un petit coup de plumeau sur le code, et tout redevient nickel. Le tapis de bienvenue est déjà déroulé pour votre retour." },
  { id: 'reveil',  e: '⏰', t: 'Réveil échelonné',                    s: "L'appli fait sa grasse matinée technique. Elle se refait une beauté et revient vite, fraîche comme au premier jour." },
];

function resolveMsg(data) {
  if (!data) return MAINT_MESSAGES[0];
  if (data.messageId === 'custom') {
    return { id: 'custom', e: data.customE || '🚧', t: data.customT || 'Petite pause technique', s: data.customS || "L'application revient très vite." };
  }
  return MAINT_MESSAGES.find(m => m.id === data.messageId) || MAINT_MESSAGES[0];
}

// Un compte est bloqué si : son rôle est ciblé ET il n'est pas dans la liste d'exceptions de ce rôle.
// (l'admin ne passe jamais par cette fonction : le garde-fou est géré en amont dans App.jsx)
function isBlocked(data, type, id) {
  if (!data || !data.on) return false;
  if (!data.targets || !data.targets[type]) return false;
  const excepted = (data.exceptions && data.exceptions[type]) || [];
  return !excepted.includes(id);
}

// ─── Écran plein écran affiché aux profils bloqués ───
function MaintenanceScreen({ data }) {
  const m = resolveMsg(data);
  const logout = () => {
    try { localStorage.removeItem('supa_session'); } catch (_) {}
    window.location.href = window.location.pathname;
  };
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, background: 'var(--bg)' }}>
      <div style={{ maxWidth: 440, width: '100%', textAlign: 'center' }}>
        <div className="D" style={{ fontSize: '1.4rem', color: 'var(--ink)', marginBottom: 28 }}>La Conciergerie</div>
        <div style={{ background: 'var(--wh)', borderRadius: 16, padding: '36px 28px', boxShadow: '0 4px 24px rgba(42,37,32,.1)', border: '1px solid var(--li)' }}>
          <div style={{ fontSize: 52, marginBottom: 16 }}>{m.e}</div>
          <div className="D" style={{ fontSize: 20, color: 'var(--ink)', marginBottom: 12 }}>{m.t}</div>
          <div style={{ fontSize: 14, color: 'var(--si)', lineHeight: 1.6 }}>{m.s}</div>
        </div>
        <div style={{ marginTop: 22, fontSize: 11, color: 'var(--si)', opacity: .7 }}>Une question urgente ? Contactez la direction directement.</div>
        <button onClick={logout} style={{ marginTop: 14, background: 'none', border: 'none', color: 'var(--si)', fontSize: 11, textDecoration: 'underline', cursor: 'pointer' }}>Se déconnecter</button>
      </div>
    </div>
  );
}

// ─── Sous-liste d'exceptions individuelles pour un rôle ciblé ───
function RoleExceptions({ roleKey, people, getName, getId, exceptions, toggleException }) {
  const [filter, setFilter] = React.useState('');
  const list = (people || []).filter(p => !p.archived);
  const filtered = filter.trim() ? list.filter(p => getName(p).toLowerCase().includes(filter.trim().toLowerCase())) : list;
  if (!list.length) return null;
  return (
    <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px dashed var(--li)' }}>
      <div style={{ fontSize: 12, color: 'var(--si)', marginBottom: 8 }}>
        Exceptions <strong>{ROLE_LABELS[roleKey]}</strong> <span style={{ opacity: .75 }}>— ces comptes gardent l'accès malgré le blocage</span>
      </div>
      {list.length > 6 && (
        <input value={filter} onChange={e => setFilter(e.target.value)} placeholder="Filtrer un nom…" style={{ marginBottom: 8, fontSize: 12, padding: '6px 10px', maxWidth: 220 }} />
      )}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {filtered.map(p => {
          const id = getId(p);
          const excepted = (exceptions || []).includes(id);
          return (
            <button key={id} onClick={() => toggleException(roleKey, id)} className="st" style={excepted ? { background: 'var(--ok)', color: 'white' } : { border: '1.5px solid var(--ls)' }}>
              {excepted ? '✓ ' : ''}{getName(p)}
            </button>
          );
        })}
        {filtered.length === 0 && <span style={{ fontSize: 12, color: 'var(--si)' }}>Aucun résultat.</span>}
      </div>
    </div>
  );
}

// ─── Panneau admin (onglet "Centre de contrôle" → Maintenance) ───
function SecMaintenance({ maintenance, setMaintenance, logAction, sess, staff, gerants, prov, clients }) {
  const data = maintenance || {};
  const defaultTargets = { staff: true, gerant: true, provider: true, client: true };
  const [pendingTargets, setPendingTargets] = React.useState(data.targets || defaultTargets);
  const [pendingExceptions, setPendingExceptions] = React.useState(data.exceptions || { staff: [], gerant: [], provider: [], client: [] });
  const [pendingMsg, setPendingMsg] = React.useState(data.messageId || 'menage');
  const [customE, setCustomE] = React.useState(data.customE || '🚧');
  const [customT, setCustomT] = React.useState(data.customT || '');
  const [customS, setCustomS] = React.useState(data.customS || '');

  const toggleTarget = (k) => setPendingTargets(t => ({ ...t, [k]: !t[k] }));
  const toggleException = (roleKey, id) => setPendingExceptions(ex => {
    const cur = ex[roleKey] || [];
    const next = cur.includes(id) ? cur.filter(x => x !== id) : [...cur, id];
    return { ...ex, [roleKey]: next };
  });

  const activate = () => {
    const anyTarget = Object.values(pendingTargets).some(Boolean);
    if (!anyTarget) { window.alert('Sélectionnez au moins un profil à bloquer.'); return; }
    if (pendingMsg === 'custom' && !customT.trim()) { window.alert('Écrivez au moins un titre pour le message personnalisé.'); return; }
    const payload = { on: true, targets: pendingTargets, exceptions: pendingExceptions, messageId: pendingMsg, customE, customT, customS, by: sess?.email || 'Admin', ts: new Date().toISOString() };
    setMaintenance(payload);
    if (typeof logAction === 'function') {
      const exCount = Object.values(pendingExceptions).reduce((n, arr) => n + (arr ? arr.length : 0), 0);
      logAction({
        action: 'maintenance_on',
        qui: sess?.email || 'Admin',
        role: 'admin',
        objet: resolveMsg(payload).t,
        cible: Object.keys(pendingTargets).filter(k => pendingTargets[k]).map(k => ROLE_LABELS[k]).join(', '),
        detail: (pendingMsg === 'custom' ? customT : '') + (exCount ? ` (${exCount} exception${exCount > 1 ? 's' : ''})` : '')
      });
    }
  };

  const deactivate = () => {
    setMaintenance({ ...data, on: false });
    if (typeof logAction === 'function') {
      logAction({ action: 'maintenance_off', qui: sess?.email || 'Admin', role: 'admin', objet: 'Mode maintenance désactivé' });
    }
  };

  const previewMsg = pendingMsg === 'custom'
    ? { e: customE || '🚧', t: customT || 'Petite pause technique', s: customS || "L'application revient très vite." }
    : MAINT_MESSAGES.find(m => m.id === pendingMsg);

  const cardBtnStyle = (active) => ({
    flexDirection: 'column', alignItems: 'flex-start', textAlign: 'left', height: 'auto', padding: '10px 12px',
    ...(active ? { border: '2px solid var(--or)', background: 'var(--cr)' } : { border: '2px solid transparent' })
  });

  const ROLE_PEOPLE = {
    staff:    { people: staff,   getName: fname,             getId: p => p.id },
    gerant:   { people: gerants, getName: p => p.name || '?', getId: p => p.id },
    provider: { people: prov,    getName: p => p.name || '?', getId: p => p.id },
    client:   { people: clients, getName: p => p.name || '?', getId: p => p.id },
  };

  const nameFor = (roleKey, id) => {
    const cfg = ROLE_PEOPLE[roleKey];
    const p = (cfg.people || []).find(x => cfg.getId(x) === id);
    return p ? cfg.getName(p) : id;
  };

  return (
    <div style={{ padding: 16, maxWidth: 760 }}>
      <div className="D" style={{ fontSize: 18, marginBottom: 4 }}>🚧 Mode maintenance</div>
      <div style={{ fontSize: 13, color: 'var(--si)', marginBottom: 20, lineHeight: 1.5 }}>
        Bloque l'accès à l'interface pour les profils sélectionnés, avec un écran d'attente. Vous (admin) gardez toujours l'accès, quel que soit l'état — c'est le seul moyen de désactiver le mode.
      </div>

      {data.on && (
        <div style={{ background: '#fde0e0', border: '1px solid #f5b0b0', borderRadius: 10, padding: '12px 16px', marginBottom: 20, fontSize: 13, color: '#8a1010' }}>
          🔴 Actuellement <strong>ACTIF</strong> pour : {Object.keys(data.targets || {}).filter(k => data.targets[k]).map(k => ROLE_LABELS[k]).join(', ') || '—'}
          {Object.entries(data.exceptions || {}).some(([, arr]) => arr && arr.length) && (
            <div style={{ fontSize: 12, marginTop: 6 }}>
              Sauf : {Object.entries(data.exceptions || {}).flatMap(([roleKey, arr]) => (arr || []).map(id => nameFor(roleKey, id))).join(', ')}
            </div>
          )}
          {data.by && <div style={{ fontSize: 11, marginTop: 4, opacity: .8 }}>Activé par {data.by}{data.ts ? ` le ${new Date(data.ts).toLocaleString('fr-FR')}` : ''}</div>}
          <div style={{ marginTop: 10 }}><button onClick={deactivate} className="btn bok sm">✅ Désactiver maintenant</button></div>
        </div>
      )}

      <div className="card" style={{ padding: 20, marginBottom: 16 }}>
        <div style={{ fontWeight: 700, marginBottom: 10, fontSize: 14 }}>1. Qui bloquer ?</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {Object.entries(ROLE_LABELS).map(([k, label]) => (
            <button key={k} onClick={() => toggleTarget(k)} className="st" style={pendingTargets[k] ? { background: 'var(--or)', color: 'white' } : { border: '1.5px solid var(--ls)' }}>
              {pendingTargets[k] ? '✓ ' : ''}{label}
            </button>
          ))}
        </div>
        {Object.entries(ROLE_LABELS).filter(([k]) => pendingTargets[k]).map(([k]) => (
          <RoleExceptions
            key={k}
            roleKey={k}
            people={ROLE_PEOPLE[k].people}
            getName={ROLE_PEOPLE[k].getName}
            getId={ROLE_PEOPLE[k].getId}
            exceptions={pendingExceptions[k]}
            toggleException={toggleException}
          />
        ))}
      </div>

      <div className="card" style={{ padding: 20, marginBottom: 16 }}>
        <div style={{ fontWeight: 700, marginBottom: 10, fontSize: 14 }}>
          2. Quel message ? <span style={{ fontWeight: 400, fontSize: 12, color: 'var(--si)' }}>(changez-en à chaque activation si vous voulez)</span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(160px,1fr))', gap: 8, marginBottom: 14 }}>
          {MAINT_MESSAGES.map(m => (
            <button key={m.id} onClick={() => setPendingMsg(m.id)} className="btn bgh sm" style={cardBtnStyle(pendingMsg === m.id)}>
              <div style={{ fontSize: 20 }}>{m.e}</div>
              <div style={{ fontWeight: 700, fontSize: 12, marginTop: 4 }}>{m.t}</div>
            </button>
          ))}
          <button onClick={() => setPendingMsg('custom')} className="btn bgh sm" style={cardBtnStyle(pendingMsg === 'custom')}>
            <div style={{ fontSize: 20 }}>✍️</div>
            <div style={{ fontWeight: 700, fontSize: 12, marginTop: 4 }}>Personnalisé</div>
          </button>
        </div>
        {pendingMsg === 'custom' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <input value={customE} onChange={e => setCustomE(e.target.value)} placeholder="Emoji (ex : 🚧)" style={{ maxWidth: 120 }} />
            <input value={customT} onChange={e => setCustomT(e.target.value)} placeholder="Titre" />
            <textarea value={customS} onChange={e => setCustomS(e.target.value)} placeholder="Sous-texte" rows={3} />
          </div>
        )}
      </div>

      <div className="card" style={{ padding: 20, marginBottom: 20 }}>
        <div style={{ fontWeight: 700, marginBottom: 10, fontSize: 14 }}>Aperçu</div>
        <div style={{ background: 'var(--bg)', borderRadius: 12, padding: 20, textAlign: 'center', border: '1px solid var(--li)' }}>
          <div style={{ fontSize: 36, marginBottom: 8 }}>{previewMsg.e}</div>
          <div className="D" style={{ fontSize: 16, marginBottom: 6 }}>{previewMsg.t}</div>
          <div style={{ fontSize: 12, color: 'var(--si)' }}>{previewMsg.s}</div>
        </div>
      </div>

      <button onClick={activate} className="btn bor" style={{ padding: '11px 22px', fontSize: 14 }}>🚧 Activer le mode maintenance</button>
    </div>
  );
}

export { MaintenanceScreen, SecMaintenance, MAINT_MESSAGES, resolveMsg, isBlocked };
